import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { merge, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';

/**
 * Burbuja flotante de soporte por WhatsApp. El numero se resuelve en `SupportWhatsappService`.
 */
@Component({
  selector: 'app-whatsapp-bubble',
  templateUrl: './whatsapp-bubble.component.html',
  styleUrls: ['./whatsapp-bubble.component.scss'],
})
export class WhatsappBubbleComponent implements OnInit, OnChanges, OnDestroy {
  /** Número en formato E.164 (con `+`). */
  @Input() e164Number: string | null = null;
  /** Nombre del edificio, usado en el mensaje pre-llenado. */
  @Input() buildingName: string | null = null;

  whatsappUrl: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    merge(
      this.refresh$,
      this.translate.onLangChange,
      this.translate.onTranslationChange,
    )
      .pipe(
        startWith(null),
        takeUntil(this.destroy$),
        switchMap(() => this.computeWhatsappUrl$()),
      )
      .subscribe((url) => {
        this.whatsappUrl = url;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.e164Number || changes.buildingName) {
      this.refresh$.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Usa `get()` para esperar la carga del JSON; `instant()` en el getter devolvía la clave. */
  private computeWhatsappUrl$(): Observable<string | null> {
    if (!this.e164Number) {
      return of(null);
    }
    const phone = this.e164Number.replace(/[^\d]/g, '');
    if (!phone) {
      return of(null);
    }
    const key = this.buildingName
      ? 'whatsappBubble.prefillFromBuilding'
      : 'whatsappBubble.prefillSupport';
    const params = this.buildingName ? { buildingName: this.buildingName } : {};
    return this.translate.get(key, params).pipe(
      map((text) => `https://wa.me/${phone}?text=${encodeURIComponent(text)}`),
    );
  }

  openWhatsapp(event: Event): void {
    event.preventDefault();
    if (!this.whatsappUrl) return;
    window.open(this.whatsappUrl, '_blank', 'noopener,noreferrer');
  }
}
